import { Trophy, Target, HeartHandshake } from "lucide-react";

const ACTION_IMG =
  "https://images.unsplash.com/photo-1759674794418-26371e3851a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHx5b3V0aCUyMHNvY2NlciUyMHRyYWluaW5nJTIwYWN0aW9ufGVufDB8fHx8MTc3NzQ0MjU0MXww&ixlib=rb-4.1.0&q=85";
const TEXTURE =
  "https://images.pexels.com/photos/17370583/pexels-photo-17370583.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
  {
    icon: Trophy,
    title: "Excellence",
    text: "Professional coaching standards from the first session to the final whistle.",
  },
  {
    icon: Target,
    title: "Discipline",
    text: "We teach the habits — punctuality, effort, repetition — that build careers.",
  },
  {
    icon: HeartHandshake,
    title: "Community",
    text: "A family where every Witbank kid gets a real shot at the beautiful game.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 lg:py-36 px-6 lg:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-10 fade-up">
          <span className="h-px w-10 bg-red-600" />
          <span className="text-xs uppercase tracking-[0.3em] text-red-600">
            Our Story
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Big text card - spans 7 cols */}
          <div className="lg:col-span-7 relative overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 p-8 lg:p-12 fade-up">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${TEXTURE})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
              }}
            />
            <div className="relative">
              <h2
                data-testid="about-title"
                className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight"
              >
                Built in Witbank.
                <br />
                <span className="text-red-600">Forged for the World.</span>
              </h2>

              <p className="mt-8 text-base text-white/75 leading-relaxed max-w-xl">
                Stars of Mpumalanga was founded with a single belief — that
                world-class football talent doesn't only come from big cities.
                It comes from kids willing to work, coaches willing to teach,
                and communities willing to back them.
              </p>

              <p className="mt-4 text-base text-white/60 leading-relaxed max-w-xl">
                We are an academy first — a school of football. Technique,
                tactics, intelligence and character are taught session by
                session, from our youngest U8s to our Pro Pathway elites.
              </p>
            </div>
          </div>

          {/* Image card - spans 5 cols */}
          <div className="lg:col-span-5 relative rounded-2xl overflow-hidden border border-white/10 group min-h-[280px] fade-up">
            <img
              src={ACTION_IMG}
              alt="Youth soccer training"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <span className="text-xs uppercase tracking-widest text-red-600 mb-2">
                Mission
              </span>
              <p className="font-heading uppercase text-2xl lg:text-3xl leading-none tracking-tight">
                Develop Players. Develop People.
              </p>
            </div>
          </div>

          {/* Values - 3 cards */}
          {VALUES.map(({ icon: Icon, title, text }, i) => (
            <div
              key={title}
              data-testid={`about-value-${i}`}
              className="lg:col-span-4 rounded-2xl border border-white/10 bg-neutral-950 p-8 hover:border-red-600/30 hover:-translate-y-1 transition-all fade-up"
            >
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-red-600/10 text-red-600 mb-6">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading uppercase text-2xl tracking-tight mb-3">
                {title}
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
